// src/core/connections/waypointSimplification.ts
/**
 * Waypoint Simplification Utilities
 * Auto-removes useless waypoints that don't change connector paths
 */

import { Coords, ConnectorAnchor, View } from 'src/types';
import { getAnchorTile } from './connectionUtils';

/**
 * Check if three points are collinear (strictly on the same line)
 * Uses cross product to determine if points are on the same line
 * 
 * @param p1 - First point
 * @param p2 - Middle point (waypoint to check)
 * @param p3 - Third point
 * @returns true if points are collinear (strict)
 */
export const arePointsCollinear = (
    p1: Coords,
    p2: Coords,
    p3: Coords
): boolean => {
    // Calculate vectors
    const v1 = { x: p2.x - p1.x, y: p2.y - p1.y };
    const v2 = { x: p3.x - p2.x, y: p3.y - p2.y };

    // Calculate cross product
    // If cross product is 0, points are collinear
    const crossProduct = v1.x * v2.y - v1.y * v2.x;

    // Strict collinearity - must be exactly 0
    return crossProduct === 0;
};

/**
 * Check if an anchor is a waypoint (not attached to an item)
 * 
 * @param anchor - Connector anchor to check
 * @returns true if anchor is a waypoint
 */
export const isWaypoint = (anchor: ConnectorAnchor): boolean => {
    return !anchor.ref.item && !!anchor.ref.tile;
};

const isPointBetween = (start: Coords, point: Coords, end: Coords) => {
    const fromStart = { x: point.x - start.x, y: point.y - start.y };
    const fromEnd = { x: point.x - end.x, y: point.y - end.y };
    return fromStart.x * fromEnd.x + fromStart.y * fromEnd.y <= 0;
};

/**
 * Produces the minimal user-defined route without changing meaningful bends.
 * Duplicate points and collinear waypoints between their neighbours are
 * removed repeatedly because one removal can make the next point redundant.
 */
export const normalizeConnectorAnchors = (
    anchors: ConnectorAnchor[],
    view: View
): ConnectorAnchor[] => {
    if (anchors.length < 3) return anchors;

    let normalized = [...anchors];
    let changed = true;

    while (changed && normalized.length >= 3) {
        changed = false;
        for (let index = 1; index < normalized.length - 1; index += 1) {
            const current = normalized[index];
            if (!isWaypoint(current)) continue;

            try {
                const previousPoint = getAnchorTile(normalized[index - 1], view);
                const currentPoint = getAnchorTile(current, view);
                const nextPoint = getAnchorTile(normalized[index + 1], view);
                if (
                    arePointsCollinear(previousPoint, currentPoint, nextPoint) &&
                    isPointBetween(previousPoint, currentPoint, nextPoint)
                ) {
                    normalized.splice(index, 1);
                    changed = true;
                    break;
                }
            } catch {
                // Keep unresolved anchors; destructive cleanup is unsafe.
            }
        }
    }

    return normalized;
};

/**
 * Simplify connector path by removing useless waypoints
 * Removes waypoints that are collinear with their neighbors
 * 
 * @param anchors - Current connector anchors
 * @param view - Current view for resolving anchor positions
 * @returns Simplified anchor array with useless waypoints removed
 */
export const simplifyConnectorPath = (
    anchors: ConnectorAnchor[],
    view: View
): ConnectorAnchor[] => {
    return normalizeConnectorAnchors(anchors, view);
};

/**
 * Check if a specific waypoint is useless (collinear with neighbors)
 * 
 * @param anchors - All connector anchors
 * @param waypointIndex - Index of waypoint to check
 * @param view - Current view
 * @returns true if waypoint is useless and should be removed
 */
export const isWaypointUseless = (
    anchors: ConnectorAnchor[],
    waypointIndex: number,
    view: View
): boolean => {
    // Can't check first or last anchor
    if (waypointIndex === 0 || waypointIndex === anchors.length - 1) {
        return false;
    }

    // Must be a waypoint (not attached to item)
    const anchor = anchors[waypointIndex];
    if (!isWaypoint(anchor)) {
        return false;
    }

    const prevAnchor = anchors[waypointIndex - 1];
    const nextAnchor = anchors[waypointIndex + 1];

    try {
        const prevPos = getAnchorTile(prevAnchor, view);
        const currentPos = getAnchorTile(anchor, view);
        const nextPos = getAnchorTile(nextAnchor, view);

        return arePointsCollinear(prevPos, currentPos, nextPos);
    } catch (error) {
        console.warn('Error checking if waypoint is useless:', error);
        return false;
    }
};

/**
 * Get count of useless waypoints in a connector
 * Useful for debugging and metrics
 * 
 * @param anchors - Connector anchors
 * @param view - Current view
 * @returns Number of useless waypoints
 */
export const countUselessWaypoints = (
    anchors: ConnectorAnchor[],
    view: View
): number => {
    let count = 0;

    for (let i = 1; i < anchors.length - 1; i++) {
        if (isWaypointUseless(anchors, i, view)) {
            count++;
        }
    }

    return count;
};
